import React, { useRef, useState, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import AnimatedMainContainer from "../../components/AnimatedMainContainer";
import { useToast } from "../../hooks/toast";
import * as Yup from "yup";
import getValidationErrors from "../../utils/getValidationErrors";
import api from "../../service";
import { useCallback } from "react";
import FormButton from "../../components/FormButton";
import { Form } from "@unform/web";
import { validateSingleFieldOnBlur } from "../../utils/formValidations";
import { base64ToBlob, downloadBlob } from "../../utils/file";

import {
  Container,
  FormContainer,
  TitleContainer,
  Title,
  FieldsContainer,
  Horizontal,
  FilterActionsContainer,
  SubmitContainer,
  GoBackContainer,
  ActionContainer,
  BadgeContainer,
  LinkButton,
  NotificationContainer,
} from "./styles";
import ReportYearEventFilter from "../../components/ReportYearEventFilter";
import useReportYearEventFilter from "../../hooks/useReportYearEventFilter";
import Badge from "../../components/Badge";
import "react-loader-spinner/dist/loader/css/react-spinner-loader.css";
import Table from "../../components/Table";

const ManageHeadbandReport = () => {
  const { token } = useSelector((state) => state.auth);
  const formRef = useRef(null);
  const intl = useIntl();
  const history = useHistory();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [showExport, setShowExport] = useState(false);
  const [filtered, setFiltered] = useState(false);

  const clearResults = useCallback(() => {
    setShowExport(false);
    setData([]);
    setFiltered(false);
  }, []);

  const {
    years,
    events,
    eventFieldDisabled,
    onChangeYear,
    resetYearEventFilter,
  } = useReportYearEventFilter({ formRef, onYearChange: clearResults });

  const schema = Yup.object().shape({
    year: Yup.string()
      .nullable()
      .required(intl.formatMessage({ id: "report_filter_year_required" })),
    event: Yup.string()
      .nullable()
      .required(intl.formatMessage({ id: "error.field_required" })),
  });

  const handleSubmit = useCallback(
    async (data) => {
      try {
        formRef.current?.setErrors({});

        setLoading(true);
        if (loading) {
          return;
        }

        await schema.validate(data, { abortEarly: false });

        api
          .patch(
            `/admin/manage-headband`,
            {},
            {
              params: {
                eventCode: data.event,
                year: data.year ? Number(data.year) : null,
              },
              headers: {
                "Content-Type": "application/json",
                Accept: "*/*",
                Authorization: `Bearer ${token}`,
              },
            }
          )
          .then((result) => {
            setData(result.data.output);
            setShowExport(true);
          })
          .catch((err) => {
            setData([]);
            setShowExport(false);
            addToast({
              type: "error",
              title: intl.formatMessage({
                id: "error",
              }),
              description: intl.formatMessage({
                id: "api.error." + err.response.data.code,
              }),
            });
          })
          .finally(() => {
            setLoading(false);
            setFiltered(true);
          });
      } catch (err) {
        setLoading(false);
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);

          formRef.current?.setErrors(errors);

          return;
        }
      }
    },
    [addToast, intl, loading, schema, token]
  );

  const getHeadbandReport = useCallback(
    (event, year) => {
      try {
        formRef.current?.setErrors({});

        setLoading(true);
        if (loading) {
          return;
        }

        api
          .patch(
            `/admin/manage-headband/export`,
            {},
            {
              params: {
                eventCode: event,
                year,
              },
              headers: {
                "Content-Type": "application/json",
                Accept: "*/*",
                Authorization: `Bearer ${token}`,
              },
            }
          )
          .then((result) => {
            const { file, name } = result.data.output;
            var blob = base64ToBlob(file, "application/vnd.ms-excel");
            downloadBlob(blob, `${name}.xlsx`);
          })
          .catch((err) => {
            addToast({
              type: "error",
              title: intl.formatMessage({
                id: "error",
              }),
              description: intl.formatMessage({
                id: "api.error." + err.response.data.code,
              }),
            });
          })
          .finally(() => {
            setLoading(false);
          });
      } catch (err) {
        setLoading(false);
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);

          formRef.current?.setErrors(errors);

          return;
        }
      }
    },
    [addToast, intl, loading, token]
  );

  const validationBlur = (name) => {
    let errors = formRef.current.getErrors();
    validateSingleFieldOnBlur(errors, schema, name, formRef);
  };

  const exportReport = () => {
    const event = formRef.current.getFieldValue("event");
    const year = formRef.current.getFieldValue("year");
    if (event == null || event === "" || year == null || year === "") {
      return;
    } else {
      getHeadbandReport(event, year);
    }
  };

  const clearFields = () => {
    formRef.current.reset();
    resetYearEventFilter();
    clearResults();
  };

  const onChangeEvent = () => {
    clearResults();
  };

  const columns = useMemo(
    () => [
      {
        name: intl.formatMessage({ id: "company_name" }),
        selector: (row) => row.accountName,
        allowOverflow: true,
        wrap: true,
        minWidth: "250px",
        sortable: true,
      },
      {
        name: intl.formatMessage({ id: "commercial_name" }),
        selector: (row) => row.accountAlias,
        allowOverflow: true,
        wrap: true,
        minWidth: "200px",
        sortable: true,
      },
      {
        name: intl.formatMessage({ id: "booth" }),
        selector: (row) => row.booth,
        allowOverflow: false,
        wrap: true,
        maxWidth: "130px",
        sortable: true,
      },
      {
        name: intl.formatMessage({ id: "area" }),
        selector: (row) => row.area,
        maxWidth: "110px",
        sortable: true,
      },
      {
        name: intl.formatMessage({ id: "front_sign" }),
        selector: (row) => row.headband,
        allowOverflow: true,
        minWidth: "200px",
        sortable: true,
      },
      {
        name: intl.formatMessage({ id: "contact" }),
        selector: (row) => row.contact,
        allowOverflow: true,
        wrap: true,
        minWidth: "155px",
        sortable: true,
      },
      {
        name: intl.formatMessage({ id: "contact_mail" }),
        selector: (row) => row.contactMail,
        allowOverflow: true,
        wrap: true,
        minWidth: "155px",
      },
      {
        name: intl.formatMessage({ id: "contact_phone" }),
        selector: (row) => row.contactPhone,
        allowOverflow: true,
        wrap: true,
        minWidth: "155px",
      },
    ],
    [intl]
  );

  return (
    <AnimatedMainContainer
      breadcrumb={intl.formatMessage({ id: "breadcumb.front_sign_report" })}
    >
      <Container>
        <FormContainer>
          <Form ref={formRef} onSubmit={handleSubmit}>
            <TitleContainer>
              <Title>
                <FormattedMessage id="export_headband_report" />
              </Title>
              <GoBackContainer>
                <ActionContainer>
                  <BadgeContainer>
                    <LinkButton onClick={() => history.push("/dashboard")}>
                      <Badge
                        color="#002244"
                        text={intl.formatMessage({ id: "goback" })}
                        fontSize="12px"
                      />
                    </LinkButton>
                  </BadgeContainer>
                </ActionContainer>
              </GoBackContainer>
            </TitleContainer>
            <FieldsContainer>
              <Horizontal>
                <ReportYearEventFilter
                  years={years}
                  events={events}
                  onChangeYear={onChangeYear}
                  onChangeEvent={onChangeEvent}
                  eventFieldDisabled={eventFieldDisabled}
                  validationBlur={validationBlur}
                />
              </Horizontal>
              <Horizontal>
                <FilterActionsContainer>
                  <SubmitContainer>
                    <FormButton
                      color="#C9C9C9"
                      type="button"
                      onClick={clearFields}
                    >
                      <FormattedMessage id="clear_fields" />
                    </FormButton>
                  </SubmitContainer>
                  <SubmitContainer>
                    <FormButton type="submit" loading={loading}>
                      <FormattedMessage id="search" />
                    </FormButton>
                  </SubmitContainer>
                  {showExport ? (
                    <SubmitContainer>
                      <FormButton
                        type="button"
                        loading={loading}
                        onClick={exportReport}
                      >
                        <FormattedMessage id="export" />
                      </FormButton>
                    </SubmitContainer>
                  ) : null}
                </FilterActionsContainer>
              </Horizontal>
            </FieldsContainer>
          </Form>
        </FormContainer>
      </Container>
      {filtered && data.length > 0 ? (
        <Table
          columns={columns}
          data={data}
          title={intl.formatMessage({ id: "results" })}
          hasPagination
        />
      ) : (
        <NotificationContainer>
          <FormattedMessage
            id={
              filtered
                ? "report_filter_no_data_for_year_and_event"
                : "need_to_filter_first"
            }
          />
        </NotificationContainer>
      )}
    </AnimatedMainContainer>
  );
};

export default ManageHeadbandReport;
